import { ClockIcon, ExclamationTriangleIcon } from '@/components/Icons'
import type { DurationWarningStep } from '../utils/meetingDurationWarning'

type CheckInCopy = {
  durationWarningTitle: string
  durationWarningBody: string
  durationWarningYesContinue: string
  durationWarningNo: string
  durationWarningEndTitle: string
  durationWarningEndBody: string
  durationWarningYesStop: string
  durationWarningNoGoBack: string
}

/**
 * Two-step duration check-in dialogs (90m / +30m): stay-using prompt, then end confirm.
 */
export function MeetingDurationCheckInModal({
  step,
  t,
  onYesContinue,
  onNoFromCheckIn,
  onNoGoBack,
  onYesStop,
}: {
  step: Exclude<DurationWarningStep, 'closed'>
  t: CheckInCopy
  onYesContinue: () => void
  onNoFromCheckIn: () => void
  onNoGoBack: () => void
  onYesStop: () => void
}) {
  const isCheckIn = step === 'checkin'

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="duration-checkin-title"
      data-testid={isCheckIn ? 'meeting-duration-checkin' : 'meeting-duration-end-confirm'}
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4"
    >
      <div className="w-full max-w-md rounded-3xl bg-white p-5 shadow-2xl dark:bg-[var(--bg-secondary)] sm:p-6">
        <div
          className={[
            'mb-4 flex h-11 w-11 items-center justify-center rounded-full',
            isCheckIn
              ? 'bg-blue-50 text-blue-600 dark:bg-[var(--accent-light)] dark:text-[var(--accent)]'
              : 'bg-red-50 text-red-600 dark:bg-red-500/15 dark:text-red-300',
          ].join(' ')}
          aria-hidden
        >
          {isCheckIn ? (
            <ClockIcon className="h-5 w-5" />
          ) : (
            <ExclamationTriangleIcon className="h-5 w-5" />
          )}
        </div>

        <p
          id="duration-checkin-title"
          className="text-lg font-bold text-slate-950 dark:text-[var(--text-primary)] sm:text-xl"
        >
          {isCheckIn ? t.durationWarningTitle : t.durationWarningEndTitle}
        </p>
        <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-[var(--text-secondary)]">
          {isCheckIn ? t.durationWarningBody : t.durationWarningEndBody}
        </p>

        <div className="mt-6 grid grid-cols-2 gap-3">
          {isCheckIn ? (
            <>
              <button
                type="button"
                data-testid="meeting-duration-checkin-no"
                onClick={onNoFromCheckIn}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-50 dark:border-[var(--border-color)] dark:bg-[var(--bg-secondary)] dark:text-[var(--text-primary)] dark:hover:bg-[var(--bg-tertiary)]"
              >
                {t.durationWarningNo}
              </button>
              <button
                type="button"
                data-testid="meeting-duration-checkin-yes"
                onClick={onYesContinue}
                className="rounded-xl bg-blue-600 px-3 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 dark:bg-[var(--accent)] dark:hover:bg-[var(--accent-hover)]"
              >
                {t.durationWarningYesContinue}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                data-testid="meeting-duration-end-go-back"
                onClick={onNoGoBack}
                className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-semibold text-slate-900 transition-colors hover:bg-slate-50 dark:border-[var(--border-color)] dark:bg-[var(--bg-secondary)] dark:text-[var(--text-primary)] dark:hover:bg-[var(--bg-tertiary)]"
              >
                {t.durationWarningNoGoBack}
              </button>
              <button
                type="button"
                data-testid="meeting-duration-end-stop"
                onClick={onYesStop}
                className="rounded-xl bg-red-600 px-3 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-red-700 dark:bg-red-500 dark:hover:bg-red-600"
              >
                {t.durationWarningYesStop}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
